import { useEffect, useRef } from 'react';
import { isTestingMode } from 'components/TestingMode';
/** Pause after the last audio chunk is sent before auto-finishing a test recording. */
const POST_LAST_CHUNK_DELAY_MS = 500;
const delay = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});
export const useWsAudioUpload = ({ eventSnapshot, connectionId, wsClient, transportOnline, recordingControl, recordingPhase, testRecordings, onTestRecordingEnded, }) => {
    const activeInputRef = useRef(null);
    const testEndedPendingRef = useRef(false);
    const audioMutedRef = useRef(false);
    audioMutedRef.current = eventSnapshot?.audioMuted ?? false;
    const shouldStream = (() => {
        if (eventSnapshot === null
            || connectionId === null
            || !transportOnline
            || wsClient === null) {
            return false;
        }
        if (eventSnapshot.selectedAudioSourceConnectionId !== connectionId) {
            return false;
        }
        const ownConnection = eventSnapshot.connections.find((c) => c.id === connectionId);
        return ownConnection?.selectedAudioSourceDeviceId != null;
    })();
    useEffect(() => {
        let cancelled = false;
        const syncRecording = async () => {
            if (!shouldStream || eventSnapshot === null || connectionId === null) {
                if (recordingPhase !== 'idle') {
                    await recordingControl.stop();
                }
                if (!cancelled) {
                    activeInputRef.current = null;
                }
                return;
            }
            const ownConnection = eventSnapshot.connections.find((c) => c.id === connectionId);
            const deviceId = ownConnection?.selectedAudioSourceDeviceId;
            if (deviceId == null) {
                if (recordingPhase !== 'idle') {
                    await recordingControl.stop();
                }
                if (!cancelled) {
                    activeInputRef.current = null;
                }
                return;
            }
            let inputSource;
            if (isTestingMode()) {
                const testRecording = testRecordings.find((r) => r.id === deviceId);
                if (testRecording === undefined) {
                    return;
                }
                inputSource = {
                    type: 'audio-file',
                    path: testRecording.path,
                    title: testRecording.title,
                };
            }
            else {
                inputSource = {
                    type: 'microphone',
                    deviceInfo: {
                        deviceId,
                        label: ownConnection?.audioSources.find((s) => s.deviceId === deviceId)?.label ?? deviceId,
                        groupId: '',
                        kind: 'audioinput',
                        toJSON: () => ({}),
                    },
                };
            }
            const sameInput = activeInputRef.current?.type === inputSource.type
                && ((inputSource.type === 'microphone'
                    && activeInputRef.current.type === 'microphone'
                    && activeInputRef.current.deviceInfo.deviceId === inputSource.deviceInfo.deviceId)
                    || (inputSource.type === 'audio-file'
                        && activeInputRef.current.type === 'audio-file'
                        && activeInputRef.current.path === inputSource.path));
            if (sameInput && recordingPhase === 'running') {
                return;
            }
            if (recordingPhase !== 'idle') {
                await recordingControl.stop();
            }
            if (cancelled) {
                return;
            }
            activeInputRef.current = inputSource;
            testEndedPendingRef.current = inputSource.type === 'audio-file';
            await recordingControl.start(inputSource);
        };
        void syncRecording();
        return () => {
            cancelled = true;
        };
    }, [
        shouldStream,
        eventSnapshot,
        connectionId,
        recordingControl,
        recordingPhase,
        testRecordings,
    ]);
    useEffect(() => {
        if (connectionId === null || wsClient === null) {
            return;
        }
        const handleAudioChunk = (event) => {
            if (audioMutedRef.current) {
                return;
            }
            wsClient.sendAudioUpload(event.detail.chunk);
        };
        const handleError = (event) => {
            console.error('Recording error:', event.detail);
            recordingControl.stop();
        };
        const handleEnded = () => {
            if (!testEndedPendingRef.current) {
                return;
            }
            testEndedPendingRef.current = false;
            void (async () => {
                await delay(POST_LAST_CHUNK_DELAY_MS);
                onTestRecordingEnded?.();
            })();
        };
        recordingControl.on('audioChunk', handleAudioChunk);
        recordingControl.on('error', handleError);
        recordingControl.on('ended', handleEnded);
        return () => {
            recordingControl.off('audioChunk', handleAudioChunk);
            recordingControl.off('error', handleError);
            recordingControl.off('ended', handleEnded);
        };
    }, [connectionId, wsClient, recordingControl, onTestRecordingEnded]);
};
